import { useLazyQuery, useMutation, FetchResult } from "@apollo/client";
import { SupplementalType } from "@app/components/supplementButton/supplementalButton";
import TestResultService from "@app/service/testResults";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { useDialog } from "@natera/platform/lib/hooks";
import React, { ReactNode, useMemo } from "react";
import {
  ProsperaOrganType,
  TestStatus,
  TestType,
  TfmrdOption,
} from "./testData/types";
import { BusinessUnits } from "./drawRequest";
import TestNotFoundModal from "@app/components/testNotFoundDialog/testNotFound";
import { ResultCodes } from "@app/service/resultCodes";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";

export enum GenderOption {
  MALE = "MALE",
  FEMALE = "FEMALE",
  NOT_REPORTED = "NOT_REPORTED",
  UNKNOWN = "UNKNOWN",
  MASKED = "MASKED",
  TWO_MALES = "TWO_MALES",
  TWO_FEMALES = "TWO_FEMALES",
  A_MALE_AND_A_FEMALE = "A_MALE_AND_A_FEMALE",
}

export enum ContentType {
  FILE = "FILE",
  VIDEO = "VIDEO",
  IMAGE = "IMAGE",
  RESOURCE = "RESOURCE",
}

export enum DocumentType {
  CLINICAL_NOTE = "CLINICAL_NOTE",
  DEMOGRAPHICS = "DEMOGRAPHICS",
  EMAIL_COMMUNICATION = "EMAIL_COMMUNICATION",
  EMR_REQUISITION = "EMR_REQUISITION",
  GC_NOTES = "GC_NOTES",
  GC_PEDIGREE_DIAGRAM = "GC_PEDIGREE_DIAGRAM",
  INSURANCE = "INSURANCE",
  PATHOLOGY_REPORT = "PATHOLOGY_REPORT",
  PEDIGREE = "PEDIGREE",
  PROACTIVE_REQUISITION = "PROACTIVE_REQUISITION",
  QUEST_REQ_FORM = "QUEST_REQ_FORM",
  REQ_FORM = "REQ_FORM",
  SAMPLE_IMAGES = "SAMPLE_IMAGES",
  SUPPLEMENTS = "SUPPLEMENTS",
  TEST_RESULTS = "TEST_RESULTS",
  TEST_RESULTS_WITHOUT_FETAL_SEX = "TEST_RESULTS_WITHOUT_FETAL_SEX",
  TRL_FORM = "TRL_FORM",
  OTHER = "OTHER",
}

type Document = {
  uid: string;
  url?: string;
  documentName?: string;
  documentType: DocumentType;
  contentType: ContentType;
  content?: string | null;
  documentReferenceId: number;
};

export type HistoricalMtmValue = {
  date?: string;
  mtm?: number;
  resultCode?: string;
};

export type NativeResult = {
  resultCode?: string;
  historicalMtmValues?: HistoricalMtmValue[];
  resultSummaryText?: string;
  currentMtmValue?: number;
  mtmDelta?: number;
};

export type TestResultDocument = {
  document?: Document | null;
  releasedToPatientDate?: string | null;
  releasedToProviderDate?: string | null;
  scheduledPatientReleaseDate?: string | null;
  resultDate?: string | null;
  providerNotes?: string | null;
  nativeResult?: NativeResult | null;
};

export type TestResult = {
  orderUidPublic: string;
  testUid: string;
  testType: TestType;
  reportGender?: boolean;
  caPnsSupplemental?: boolean;
  resultCode?: string;
  fetalSex?: GenderOption;
  releasedToPatientDate?: string | null;
  organ?: ProsperaOrganType;
  documents?: [
    {
      uid: string;
      documentName: string;
      contentType: SupplementalType;
    },
  ];
  resultDocuments?: TestResultDocument[];
  latestResultDocumentUid?: string;
  businessUnit: BusinessUnits;
  isTfMrdActive?: boolean;
  tfMrdOrderingOption?: TfmrdOption;
  isReflexed?: boolean;
};

export type SupplementalMaterial = {
  uid: string;
  url?: string | null;
  documentName: string;
  contentType: string;
};

export interface TestResultController {
  isLoading: boolean;
  testResult?: TestResult;
  testResultInfoToken?: string;
  getTestResultInfoFromToken: (
    token: string
  ) => Promise<{ orderUidPublic: string; testUid: string } | undefined>;
  setTestResultInfoToken: (token: string) => void;
  getTestResult: (
    orderUidPublic: string,
    testUid: string
  ) => Promise<TestResult | undefined>;
  getTestResultError?: string;
  getSupplementalMaterials: (
    orderUidPublic: string,
    testUid: string,
    uid: string
  ) => Promise<SupplementalMaterial | undefined>;
  getSupplementMaterialIsLoading: boolean;
  getSupplementMaterialError?: string;
  accessTestResults: (
    orderUidPublic: string,
    testUid: string,
    testType: TestType,
    documentUid: string,
    viewStatus?: TestStatus
  ) => Promise<FetchResult<{ success: boolean }>>;
  supplementalMaterials?: SupplementalMaterial;
}
export const Context = React.createContext<TestResultController>({
  isLoading: false,
  testResult: undefined,
  getTestResult: () => Promise.reject(),
  getTestResultInfoFromToken: () => Promise.reject(),
  setTestResultInfoToken: () => undefined,
  getSupplementalMaterials: () => Promise.reject(),
  getSupplementMaterialIsLoading: false,
  accessTestResults: async () => ({}),
  supplementalMaterials: undefined,
});

Context.displayName = "TestResultContext";

const TestResultProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [testResult, setTestResult] = React.useState<TestResult | undefined>();
  const [supplementalMaterials, setSupplementalMaterials] = React.useState<
    SupplementalMaterial | undefined
  >();
  const [testResultInfoToken, setTestResultInfoToken] = React.useState("");

  // Fallback for the DelayedResultReleaseGuard: open the same modal when the server
  // returns TEST_NOT_FOUND (in case a result-fetching route isn't wrapped)
  const testNotFoundDialog = useDialog(TestNotFoundModal);

  const [
    getTestResultQuery,
    { loading: getTestResultIsLoading, error: getTestResultError },
  ] = useLazyQuery<{
    getTestResult: TestResult;
  }>(TestResultService.getTestResult(), {
    onError: (error) => {
      const code = getGraphQLErrorCode(error);
      if (code === ResultCodes.TEST_NOT_FOUND) {
        testNotFoundDialog.open({});
      }
    },
  });

  const [
    getTestResultInfoFromToken,
    { loading: getTestResultInfoFromTokenIsLoading },
  ] = useLazyQuery<{
    getTestResultInfoFromToken: { orderUidPublic: string; testUid: string };
  }>(TestResultService.getTestResultInfoFromToken());

  const getTestResult = async (orderUidPublic: string, testUid: string) => {
    const result = await getTestResultQuery({
      variables: { orderUidPublic, testUid },
    });
    setTestResult(result.data?.getTestResult);
    return result.data?.getTestResult;
  };

  const handleGetTestResultInfoFromToken = async (token: string) => {
    const response = await getTestResultInfoFromToken({ variables: { token } });
    return response.data?.getTestResultInfoFromToken;
  };

  const handleSetTestResultInfoToken = (token: string) => {
    setTestResultInfoToken(token);
  };

  const [
    getSupplementMaterial,
    {
      loading: getSupplementMaterialIsLoading,
      error: getSupplementMaterialError,
    },
  ] = useLazyQuery<{
    getSupplementalMaterial: SupplementalMaterial;
  }>(TestResultService.getSupplementMaterial());

  const getSupplementalMaterials = async (
    orderUidPublic: string,
    testUid: string,
    uid: string
  ) => {
    const result = await getSupplementMaterial({
      variables: { orderUidPublic, testUid, documentUid: uid },
    });
    setSupplementalMaterials(result.data?.getSupplementalMaterial);

    return result.data?.getSupplementalMaterial;
  };

  const [accessTestResults] = useMutation(
    TestResultService.AccessTestResults()
  );

  const handleAccessTestResults = async (
    orderUidPublic: string,
    testUid: string,
    testType: TestType,
    documentUid: string,
    viewStatus?: TestStatus
  ) => {
    const response = await accessTestResults({
      variables: {
        accessTestResults: {
          orderUidPublic,
          testUid,
          testType,
          documentUid,
          viewStatus,
        },
      },
    });

    if (response && response.data) {
      return response.data.accessTestResults;
    }
    throw Error();
  };

  const isLoading =
    getTestResultIsLoading || getTestResultInfoFromTokenIsLoading;

  const testResultController: TestResultController = useMemo(
    () => ({
      isLoading,
      testResult,
      testResultInfoToken,
      getTestResult,
      getTestResultError:
        getTestResultError?.networkError?.message ||
        getTestResultError?.graphQLErrors[0].message,
      getTestResultInfoFromToken: handleGetTestResultInfoFromToken,
      setTestResultInfoToken: handleSetTestResultInfoToken,
      getSupplementalMaterials,
      accessTestResults: handleAccessTestResults,
      getSupplementMaterialIsLoading,
      getSupplementMaterialError:
        getSupplementMaterialError?.networkError?.message ||
        getSupplementMaterialError?.graphQLErrors[0].message,
      supplementalMaterials,
    }),
    [
      isLoading,
      testResult,
      testResultInfoToken,
      getTestResult,
      getTestResultError,
      handleGetTestResultInfoFromToken,
      handleSetTestResultInfoToken,
      getSupplementalMaterials,
      handleAccessTestResults,
      getSupplementMaterialIsLoading,
      getSupplementMaterialError,
      supplementalMaterials,
    ]
  );

  return (
    <Context.Provider value={testResultController}>
      <LoadingContext
        data-testid="result-details-page-loader"
        isLoading={isLoading}
      >
        {testNotFoundDialog.getDialog()}
        {children}
      </LoadingContext>
    </Context.Provider>
  );
};

export default TestResultProvider;
